import { useCallback, useEffect, useRef, useState, type DependencyList } from 'react'
import { endOfDay, startOfDay } from 'date-fns'
import { useAuth } from '@/contexts/AuthContext'
import {
  applyChatContactScopeFilter,
  contactMatchesScope,
  shouldApplyContactScope,
} from '@/lib/chatContactScope'

type PageResult<T> = {
  data: T[] | null
  error: { message?: string } | null
}

export async function fetchAllRows<T>(
  build: (from: number, to: number) => PromiseLike<PageResult<T>>,
  pageSize = 1000,
): Promise<T[]> {
  const rows: T[] = []

  for (let from = 0; ; from += pageSize) {
    const { data, error } = await build(from, from + pageSize - 1)
    if (error) throw new Error(error.message || 'Erro ao consultar o relatório')

    const page = data || []
    rows.push(...page)
    if (page.length < pageSize) break
  }

  return rows
}

export function useAsyncReport<T>(
  factory: () => Promise<T>,
  fallback: T,
  deps: DependencyList,
) {
  const factoryRef = useRef(factory)
  factoryRef.current = factory
  const [data, setData] = useState<T>(fallback)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const requestRef = useRef(0)

  const run = useCallback(async () => {
    const requestId = ++requestRef.current
    setLoading(true)
    setError(null)

    try {
      const result = await factoryRef.current()
      if (requestId === requestRef.current) setData(result)
    } catch (err) {
      if (requestId === requestRef.current) {
        setData(fallback)
        setError(err instanceof Error ? err.message : 'Erro ao carregar relatório')
      }
    } finally {
      if (requestId === requestRef.current) setLoading(false)
    }
  }, [fallback])

  useEffect(() => {
    void run()
    return () => {
      requestRef.current += 1
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return { data, loading, error, refetch: run }
}

export function useReportScope(from: Date, to: Date) {
  const { user } = useAuth()

  return {
    user,
    accountId: user?.account_id || user?.account?.id || null,
    fromIso: startOfDay(from).toISOString(),
    toIso: new Date(endOfDay(to).getTime() + 1).toISOString(),
    applyContactScope: <T,>(query: T): T =>
      applyChatContactScopeFilter(query, user) as T,
    contactMatchesScope: (contact: { attribution_id: string | null }) =>
      contactMatchesScope(user, contact),
    isContactScopeRestricted: shouldApplyContactScope(user),
  }
}

export function normalizeEmbed<T>(value: T | T[] | null | undefined): T | null {
  if (Array.isArray(value)) return value[0] ?? null
  return value ?? null
}
